'use client';

import { PropsWithChildren, ReactNode, useMemo } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { usePathname, useRouter } from '@flipster/i18n/routing';
import { createSingleChoice } from '@flipster/react/design-system/chip';
import { createTabsComponent } from '@flipster/react/design-system/tabs';
import { css } from '@flipster/styled-system/css';
import segment from '@flipster/utils/segment';
import { HistoryEvent } from '@flipster/utils/segment/events/history';

import { useCheckSpotOnly } from '../../shared/regulation';
import { HistoryTabGroup, getHistoryTabList, getTabList } from './constants';

const Tabs = createTabsComponent<HistoryTabGroup>();
const Choice = createSingleChoice<string>();

interface Props extends PropsWithChildren {
  title: ReactNode;
  isWebview?: boolean;
  historyTabGroup: HistoryTabGroup;
}

const HistoryPageLayout = ({ title, children, isWebview, historyTabGroup }: Props) => {
  const router = useRouter();
  const pathname = usePathname();

  const isSpotOnly = useCheckSpotOnly();
  const tabList = useMemo(() => getHistoryTabList(isSpotOnly), [isSpotOnly]);
  const subTabList = useMemo(() => getTabList(historyTabGroup, isSpotOnly), [historyTabGroup, isSpotOnly]);

  return (
    <main
      className={css({
        p: isWebview ? '0 0 48px' : { base: '32px 0 56px', tablet: '0 0 150px' },
        tablet: {
          w: 'legacyHdMaxWidth',
          m: '80px auto',
        },
      })}
    >
      {isWebview && (
        <div
          className={css({
            textStyle: 'Title/L',
            color: 'inverse.white',
            p: '32px 16px 24px',
          })}
        >
          <h1>{title}</h1>
        </div>
      )}
      {!isWebview && (
        <>
          <h1
            className={css({
              textStyle: 'Display/L',
              color: 'inverse.white',
              mb: 30,
              tabletDown: { display: 'none' },
            })}
          >
            <FormattedMessage id='Autogen_common.r251126.s_History' />
          </h1>
          <h2
            className={css({
              textStyle: 'Title/L',
              color: 'inverse.white',
              px: 16,
              mb: 24,
              tablet: { display: 'none' },
            })}
          >
            {title}
          </h2>
          <div className={css({ mb: 20, tabletDown: { display: 'none' } })}>
            <Tabs
              border
              type='responsive'
              value={historyTabGroup || 'Transaction'}
              onClick={(tab) => {
                // clicking selected tab again should clean all sub-filters.
                // using onChange prop blocks this feature.
                segment.track(HistoryEvent.HistoryTypeClick, { history_type: tab || '' });
                const tabs = getTabList(tab, isSpotOnly);
                router.push(tabs[0].value);
              }}
            >
              {tabList.map((tab) => (
                <Tabs.Tab key={tab.value} label={tab.label} value={tab.value} />
              ))}
            </Tabs>
          </div>
          <div className={css({ tabletDown: { display: 'none' } })}>
            <Choice value={pathname}>
              {subTabList.map((tab) => (
                <Choice.Chip
                  key={tab.value}
                  value={tab.value}
                  label={tab.label}
                  // clicking selected chip again should clean all sub-filters.
                  // using onChange prop on parent (Choice) blocks this feature.
                  onClick={() => router.push(tab.value)}
                />
              ))}
            </Choice>
          </div>
        </>
      )}
      {children}
    </main>
  );
};

export default HistoryPageLayout;
